import { useCallback, useEffect, useRef, useState } from 'react';
import type { JobStatusResponse } from '~/lib/api';
import { api } from '~/lib/api';
import { POLL_TERMINAL } from '~/lib/jobStatus';

const TERMINAL = new Set<string>(POLL_TERMINAL);
const POLL_MS = 5000; // 12.6: poll ≥5 s while non-terminal

/** useJob (12.6): polls a job until terminal, then stops. */
export function useJob(jobId: string | null) {
  const [job, setJob] = useState<JobStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  /*
    A generation counter, bumped whenever the job being watched changes or the hook
    unmounts. Without it a response that was already in flight still calls setJob when it
    lands, so opening job B while job A's request is outstanding paints A over B — and on
    a slow connection A wins, because it started first. Clearing the interval does not
    help: the request is already gone.
  */
  const generation = useRef(0);

  const refresh = useCallback(async () => {
    if (!jobId) return;
    const mine = generation.current;
    try {
      const j = await api.getJob(jobId);
      if (generation.current !== mine) return;
      setJob(j);
      setError(null);
      if (TERMINAL.has(j.status) && timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    } catch (e) {
      if (generation.current !== mine) return;
      setError(e instanceof Error ? e.message : 'load failed');
    }
  }, [jobId]);

  useEffect(() => {
    generation.current += 1;
    setJob(null);
    setError(null);
    void refresh();
    timer.current = setInterval(() => void refresh(), POLL_MS);
    return () => {
      // Bumped on the way out too, so a response landing after unmount is dropped rather
      // than setting state on a component that is gone.
      generation.current += 1;
      if (timer.current) clearInterval(timer.current);
    };
  }, [refresh]);

  return { job, error, refresh };
}
